import React, { useEffect, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { API } from "../../../App";
import { getAuthConfig, SectionCard, SectionError, fmtMoney } from "./dashUtils";

const METRICS = [
  { value: "collected", label: "Money Collected", money: true },
  { value: "jobs_completed", label: "Jobs Completed" },
  { value: "jobs_created", label: "Jobs Created" },
  { value: "calls", label: "Inbound Calls" },
  { value: "new_customers", label: "New Customers" },
  { value: "invoices", label: "Invoice Sales", money: true },
  { value: "tax_collected", label: "Tax Collected", money: true },
];
const COLORS = ["#2563eb", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#06b6d4"];
const JOB_BASED = ["collected", "jobs_completed", "jobs_created", "tax_collected", "invoices"];

const PerformanceChart = ({ locationId, multiLocation }) => {
  const [metric, setMetric] = useState("collected");
  const [days, setDays] = useState(7);
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const canCompare = multiLocation && locationId === "all" && JOB_BASED.includes(metric);

  const fetchChart = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/store/dashboard-v2/chart`, {
        ...getAuthConfig(),
        params: { metric, days, location_id: locationId, compare: canCompare && compare ? 1 : 0 },
      });
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChart(); /* eslint-disable-next-line */ }, [metric, days, compare, locationId]);

  const metricDef = METRICS.find((m) => m.value === metric) || {};
  const rows = (data?.labels || []).map((day, i) => {
    const row = { day: day.slice(5) };
    (data?.series || []).forEach((s) => { row[s.name] = s.data[i]; });
    return row;
  });
  const seriesNames = (data?.series || []).map((s) => s.name);

  return (
    <SectionCard title="Performance" icon={TrendingUp} testid="performance-chart"
      action={
        <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
          {canCompare && (
            <button
              onClick={() => setCompare((v) => !v)}
              className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${compare ? "bg-blue-600 text-white border-blue-600" : "text-muted-foreground border-border hover:text-foreground"}`}
              data-testid="chart-compare-toggle"
            >
              By location
            </button>
          )}
          <div className="flex rounded-lg border border-border overflow-hidden">
            {[7, 30].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-2 py-1 text-[10px] font-medium ${days === d ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`chart-days-${d}`}>
                {d}d
              </button>
            ))}
          </div>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="chart-metric-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      }>
      {error ? (
        <SectionError label="chart data" onRetry={fetchChart} />
      ) : loading && !data ? (
        <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
      ) : (
        <div className="h-52 min-h-[208px]" data-testid="chart-area">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 6, right: 6, left: metricDef.money ? 4 : -14, bottom: 0 }}>
              <defs>
                {seriesNames.map((n, i) => (
                  <linearGradient key={n} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false}
                tickFormatter={(v) => (metricDef.money ? `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}` : v)} />
              <Tooltip
                formatter={(v, name) => [metricDef.money ? fmtMoney(v) : v, name === "total" ? metricDef.label : name]}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {seriesNames.map((n, i) => (
                <Area key={n} type="monotone" dataKey={n} stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                  fill={`url(#grad-${i})`} dot={false} name={n === "total" ? metricDef.label : n} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
};

export default PerformanceChart;
